import { useEffect, useState } from "react";
import { Sparkles, ShieldCheck, BellRing, IndianRupee } from "lucide-react";
import { api, imageUrl } from "@/lib/api";

const DEFAULTS = [
  { icon: ShieldCheck, title: "Zero brokerage", text: "Deal directly with verified owners and dealers." },
  { icon: BellRing, title: "Instant alerts", text: "Get an email the moment a matching home is listed." },
  { icon: IndianRupee, title: "EMI upfront", text: "See your monthly instalment before you enquire." },
];

export const HomeBanner = () => {
  const [banners, setBanners] = useState([]);
  const [i, setI] = useState(0);

  useEffect(() => {
    api.get("/banners").then((r) => setBanners(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (banners.length < 2) return;
    const t = setInterval(() => setI((v) => (v + 1) % banners.length), 6000);
    return () => clearInterval(t);
  }, [banners.length]);

  if (!banners.length)
    return (
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 mt-8 sm:mt-10">
        <div className="grid gap-4 sm:grid-cols-3" data-testid="home-default-banner">
          {DEFAULTS.map(({ icon: Icon, title, text }) => (
            <div key={title} className="flex items-start gap-3.5 rounded-2xl border border-border p-5 hover:border-foreground/25 transition-colors duration-200">
              <span className="mt-0.5 rounded-xl bg-primary/10 p-2.5 text-primary">
                <Icon className="h-5 w-5" />
              </span>
              <div>
                <p className="font-display text-[15px]">{title}</p>
                <p className="mt-1 text-[13px] text-muted-foreground">{text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );

  const b = banners[i % banners.length];
  const body = (
    <div className="relative overflow-hidden rounded-2xl bg-foreground" data-testid="home-promo-banner">
      {b.image_id && (
        <>
          <img
            src={imageUrl(b.image_id)}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full scale-110 object-cover blur-2xl opacity-50"
          />
          <img
            src={imageUrl(b.image_id)}
            alt={b.title}
            className="relative mx-auto max-h-[220px] sm:max-h-[300px] md:max-h-[440px] w-auto max-w-full object-contain"
          />
        </>
      )}
      <div className={`${b.image_id ? "absolute inset-x-0 bottom-0" : ""} flex flex-col justify-end gap-1 sm:gap-1.5 bg-gradient-to-t from-black/80 via-black/45 to-transparent p-5 sm:p-6 md:p-10 md:pt-20`}>
        <p className="flex items-center gap-2 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.28em] text-white/80">
          <Sparkles className="h-3.5 w-3.5" /> Houses7 promo
        </p>
        <p className="font-display text-xl sm:text-2xl md:text-3xl text-white">{b.title}</p>
        {b.subtitle && <p className="max-w-xl text-[12px] sm:text-[13px] md:text-sm text-white/85">{b.subtitle}</p>}
        {b.cta_label && (
          <span className="mt-3 w-fit rounded-lg bg-primary px-5 py-2.5 text-[13px] font-bold text-primary-foreground">
            {b.cta_label}
          </span>
        )}
      </div>
      {banners.length > 1 && (
        <div className="absolute bottom-4 right-5 flex gap-1.5">
          {banners.map((x, idx) => (
            <button
              key={x.banner_id}
              onClick={() => setI(idx)}
              aria-label={`Banner ${idx + 1}`}
              className={`h-2 rounded-full transition-all duration-300 ${idx === i ? "w-6 bg-white" : "w-2 bg-white/50"}`}
              data-testid={`banner-dot-${idx}`}
            />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 mt-8 sm:mt-10">
      {b.link ? (
        <a href={b.link} target="_blank" rel="noreferrer" data-testid="home-banner-link">
          {body}
        </a>
      ) : (
        body
      )}
    </div>
  );
};

export default HomeBanner;
